import { useMemo, useState } from "react";
import {
  Divider,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "react-query";
import CustomTableBody from "../utilis/component/CustomTableBody";
import { clinicId } from "../constants";
import { getAllFeedback } from "./services";
import { FeedbackItem, FeedbackListSearchParams } from "./types";

const headings = [
  "SL No",
  "Patient Name",
  "Mobile No",
  "Feedback",
  "Patient Master ID",
];

const normalizeFeedbacks = (
  feedbacks?: FeedbackItem[] | FeedbackItem,
): FeedbackItem[] => {
  if (!feedbacks) return [];
  return Array.isArray(feedbacks) ? feedbacks : [feedbacks];
};

const FeedbackListPage = () => {
  const [searchParams, setSearchParams] = useState<FeedbackListSearchParams>({
    clinicId,
    sortOrder: "",
    sortField: "",
    pageNumber: 0,
    pageSize: 10,
  });

  const { data, isLoading } = useQuery(
    ["patient-feedback-list", searchParams],
    () => getAllFeedback(searchParams),
    {
      refetchOnWindowFocus: false,
    },
  );

  const feedbackList = useMemo(
    () => normalizeFeedbacks(data?.response?.feedbacks),
    [data?.response?.feedbacks],
  );

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    pageNumber: number,
  ) => {
    setSearchParams((prev) => ({
      ...prev,
      pageNumber,
    }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSearchParams((prev) => ({
      ...prev,
      pageNumber: 0,
      pageSize: parseInt(event.target.value, 10),
    }));
  };

  return (
    <Paper className="m-3 mt-6">
      <div className="flex justify-between p-3">
        <Typography variant="h6" color="default">
          Patient Feedback List
        </Typography>
      </div>
      <Divider />
      <TableContainer className="border">
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {headings.map((heading) => (
                <TableCell key={heading} className="font-bold">
                  {heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <CustomTableBody
            alert="No feedback found"
            dataLoading={isLoading}
            isData={feedbackList.length > 0}
            totalSpan={{
              row: 5,
              col: headings.length,
            }}
          >
            {feedbackList.map((item, index) => (
              <TableRow key={`${item.feedBackId}-${index}`}>
                <TableCell>
                  {searchParams.pageNumber * searchParams.pageSize + index + 1}
                </TableCell>
                <TableCell>{item.patientName || "-"}</TableCell>
                <TableCell>{item.mobileNo || "-"}</TableCell>
                <TableCell sx={{ maxWidth: 420, whiteSpace: "normal" }}>
                  {item.feedback || "-"}
                </TableCell>
                <TableCell>{item.patientMasterId ?? "-"}</TableCell>
              </TableRow>
            ))}
          </CustomTableBody>
        </Table>
        <div className="flex justify-end">
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={data?.response?.totalCount || 0}
            rowsPerPage={searchParams.pageSize}
            page={searchParams.pageNumber}
            sx={{ border: 0 }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </TableContainer>
    </Paper>
  );
};

export default FeedbackListPage;
